import { formatCurrency } from '@/utils/formatCurrency';
import {
  EMPTY_RETAIL_KPI_METRICS,
  resolveRetailKpiMetrics,
  type WorkspaceRetailKpiMetrics,
} from '@/utils/workspaceAdProductMetrics';
import { percentChangeVsBaseline } from '@/api/reportCompareMetrics';

export type ProductTotals = {
  impressions: number;
  clicks: number;
  cost: number;
  purchases30d: number;
  sales30d: number;
};

export type SnapshotDerived = {
  aov: number;
  cr: number;
  acos: number;
  cpm: number;
  rpc: number;
  cpo: number;
  troas: number;
};

export type SnapshotTotals = ProductTotals & {
  ctr: number;
  cpc: number;
  roas: number;
};

export type SnapshotPeriod = {
  range: { startDate?: string; endDate?: string };
  totals: SnapshotTotals;
  derived: SnapshotDerived;
  byAdProduct: Record<string, ProductTotals>;
  rowCount: number;
};

export type SnapshotFreshness = {
  grain?: string;
  reconciledThrough?: string | null;
  provisionalDates: string[];
  dataThrough?: string | null;
  failedReportDates: string[];
};

export type SnapshotParsed = {
  current: SnapshotPeriod;
  previous: SnapshotPeriod;
  truncated: boolean;
  freshness: SnapshotFreshness | null;
};

const EMPTY_PRODUCT: ProductTotals = {
  impressions: 0,
  clicks: 0,
  cost: 0,
  purchases30d: 0,
  sales30d: 0,
};

const EMPTY_DERIVED: SnapshotDerived = {
  aov: 0,
  cr: 0,
  acos: 0,
  cpm: 0,
  rpc: 0,
  cpo: 0,
  troas: 0,
};

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function parseProductTotals(raw: unknown): ProductTotals {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_PRODUCT };
  const o = raw as Record<string, unknown>;
  return {
    impressions: num(o.impressions),
    clicks: num(o.clicks),
    cost: num(o.cost),
    purchases30d: num(o.purchases30d),
    sales30d: num(o.sales30d),
  };
}

function parseTotals(raw: unknown): SnapshotTotals {
  if (!raw || typeof raw !== 'object') {
    return { ...EMPTY_PRODUCT, ctr: 0, cpc: 0, roas: 0 };
  }
  const o = raw as Record<string, unknown>;
  const base = parseProductTotals(raw);
  return {
    ...base,
    ctr: num(o.ctr),
    cpc: num(o.cpc),
    roas: num(o.roas),
  };
}

function parseDerived(raw: unknown): SnapshotDerived {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_DERIVED };
  const o = raw as Record<string, unknown>;
  return {
    aov: num(o.aov),
    cr: num(o.cr),
    acos: num(o.acos),
    cpm: num(o.cpm),
    rpc: num(o.rpc),
    cpo: num(o.cpo),
    troas: num(o.troas),
  };
}

function parsePeriod(raw: unknown): SnapshotPeriod {
  if (!raw || typeof raw !== 'object') {
    return {
      range: {},
      totals: parseTotals(null),
      derived: { ...EMPTY_DERIVED },
      byAdProduct: {},
      rowCount: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  const byAdProduct: Record<string, ProductTotals> = {};
  if (o.byAdProduct && typeof o.byAdProduct === 'object') {
    for (const [key, value] of Object.entries(o.byAdProduct as Record<string, unknown>)) {
      byAdProduct[key] = parseProductTotals(value);
    }
  }
  return {
    range: (o.range as { startDate?: string; endDate?: string }) || {},
    totals: parseTotals(o.totals),
    derived: parseDerived(o.derived),
    byAdProduct,
    rowCount: num(o.rowCount),
  };
}

function parseFreshness(raw: unknown): SnapshotFreshness | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const provisional = Array.isArray(o.provisionalDates)
    ? o.provisionalDates.filter((value): value is string => typeof value === 'string')
    : [];
  const failed = Array.isArray(o.failedReportDates)
    ? o.failedReportDates.filter((value): value is string => typeof value === 'string')
    : [];
  return {
    grain: typeof o.grain === 'string' ? o.grain : undefined,
    reconciledThrough: typeof o.reconciledThrough === 'string' ? o.reconciledThrough : null,
    provisionalDates: provisional,
    dataThrough: typeof o.dataThrough === 'string' ? o.dataThrough : null,
    failedReportDates: failed,
  };
}

export function parseSnapshotPayload(data: unknown): SnapshotParsed {
  if (!data || typeof data !== 'object') {
    return {
      current: parsePeriod(null),
      previous: parsePeriod(null),
      truncated: false,
      freshness: null,
    };
  }
  const raw = data as Record<string, unknown>;
  return {
    current: parsePeriod(raw.current),
    previous: parsePeriod(raw.previous),
    truncated: Boolean(raw.truncated),
    freshness: parseFreshness(raw.freshness),
  };
}

export function mergeSbSd(byAdProduct: Record<string, ProductTotals>): ProductTotals {
  const sb = byAdProduct.sponsored_brands ?? EMPTY_PRODUCT;
  const sd = byAdProduct.sponsored_display ?? EMPTY_PRODUCT;
  return {
    impressions: sb.impressions + sd.impressions,
    clicks: sb.clicks + sd.clicks,
    cost: sb.cost + sd.cost,
    purchases30d: sb.purchases30d + sd.purchases30d,
    sales30d: sb.sales30d + sd.sales30d,
  };
}

export type OrganicPaidBuckets = {
  organic: ProductTotals;
  paid: ProductTotals;
};

export type OrganicPaidPeriodView = {
  buckets: OrganicPaidBuckets;
  salesTotal: number;
  ordersTotal: number;
  gvTotal: number;
  aov: number;
  cr: number;
  taos: number;
  acos: number;
  /** Shared ad spend for TACOS. Null on demo (sum bucket cost instead). */
  taosSpend: number | null;
  adsExceedRetail: boolean;
  attributedAdSales: number;
  adsOrdersExceedRetail: boolean;
  attributedAdOrders: number;
};

/** TACOS% = ad spend ÷ total sales × 100. */
export function tacosPercent(
  spend: number | null,
  buckets: OrganicPaidBuckets
): number {
  const cost = spend ?? buckets.organic.cost + buckets.paid.cost;
  const sales = buckets.organic.sales30d + buckets.paid.sales30d;
  return sales > 0 ? (cost / sales) * 100 : 0;
}

function adsAsProduct(period: SnapshotPeriod): ProductTotals {
  const t = period.totals;
  return {
    impressions: t.impressions,
    clicks: t.clicks,
    cost: t.cost,
    purchases30d: t.purchases30d,
    sales30d: t.sales30d,
  };
}

export function organicPaidFromSnapshot(
  period: SnapshotPeriod,
  retail: WorkspaceRetailKpiMetrics = EMPTY_RETAIL_KPI_METRICS,
  options: { demo: boolean }
): OrganicPaidPeriodView {
  if (options.demo) {
    const organic = period.byAdProduct.sponsored_products ?? EMPTY_PRODUCT;
    const paid = mergeSbSd(period.byAdProduct);
    return {
      buckets: { organic: { ...organic }, paid },
      salesTotal: period.totals.sales30d,
      ordersTotal: period.totals.purchases30d,
      gvTotal: period.totals.clicks,
      aov: period.derived.aov,
      cr: period.derived.cr,
      taos: period.derived.troas,
      acos: period.derived.acos,
      taosSpend: null,
      adsExceedRetail: false,
      attributedAdSales: period.totals.sales30d,
      adsOrdersExceedRetail: false,
      attributedAdOrders: period.totals.purchases30d,
    };
  }

  const ads = adsAsProduct(period);
  const resolved = resolveRetailKpiMetrics(ads, retail);
  const mappedGv = retail.glanceViews != null;
  const glanceViews = mappedGv ? Number(retail.glanceViews) : 0;
  const gvTotal = mappedGv ? Math.max(glanceViews, ads.clicks) : ads.clicks;
  const paidClicks = ads.clicks;
  const organicClicks = mappedGv ? Math.max(0, gvTotal - paidClicks) : 0;
  const vendorSales = retail.totalSales != null;
  const taosSpend = ads.cost;
  const taos = vendorSales
    ? taosSpend > 0
      ? resolved.totalSales / taosSpend
      : 0
    : period.derived.troas;
  const aov = resolved.totalOrders > 0 ? resolved.totalSales / resolved.totalOrders : 0;
  const cr = gvTotal > 0 ? (resolved.totalOrders / gvTotal) * 100 : period.derived.cr;
  const acos =
    ads.sales30d > 0 ? (ads.cost / ads.sales30d) * 100 : period.derived.acos;

  return {
    buckets: {
      organic: {
        impressions: 0,
        clicks: organicClicks,
        cost: 0,
        purchases30d: resolved.organicOrders,
        sales30d: resolved.organicSales,
      },
      paid: {
        ...ads,
        clicks: paidClicks,
        purchases30d: resolved.paidOrders,
        sales30d: resolved.paidSales,
      },
    },
    salesTotal: resolved.totalSales,
    ordersTotal: resolved.totalOrders,
    gvTotal,
    aov,
    cr,
    taos,
    acos,
    taosSpend: vendorSales ? taosSpend : null,
    adsExceedRetail: resolved.adsExceedRetail,
    attributedAdSales: resolved.attributedAdSales,
    adsOrdersExceedRetail: resolved.adsOrdersExceedRetail,
    attributedAdOrders: resolved.attributedAdOrders,
  };
}

export function organicVsPaidSplit(
  buckets: OrganicPaidBuckets,
  metric: keyof ProductTotals,
  total?: number
): { organic: number; paid: number; organicPct: number; paidPct: number } {
  const organic = buckets.organic[metric] ?? 0;
  const paid = buckets.paid[metric] ?? 0;
  const denom =
    total != null && Number.isFinite(total) && total > 0 ? total : organic + paid;
  if (denom <= 0) {
    return { organic, paid, organicPct: 0, paidPct: 0 };
  }
  return {
    organic,
    paid,
    organicPct: (organic / denom) * 100,
    paidPct: (paid / denom) * 100,
  };
}

export function organicPaidHasData(
  view: OrganicPaidPeriodView,
  adsRowCount: number
): boolean {
  if (adsRowCount > 0) return true;
  return view.salesTotal > 0 || view.ordersTotal > 0 || view.gvTotal > 0;
}

export function derivedForProduct(totals: ProductTotals): Pick<SnapshotDerived, 'aov' | 'cr'> {
  return {
    aov: totals.purchases30d > 0 ? totals.sales30d / totals.purchases30d : 0,
    cr: totals.clicks > 0 ? (totals.purchases30d / totals.clicks) * 100 : 0,
  };
}

export { percentChangeVsBaseline };

export function formatSnapshotCurrency(value: number | null): string {
  return formatCurrency(value, undefined, {
    empty: '—',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export function formatSnapshotCurrencyPrecise(value: number | null): string {
  return formatCurrency(value, undefined, {
    empty: '—',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatSnapshotNumber(value: number | null, fractionDigits = 0): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatSnapshotPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(2)}%`;
}

export function formatPctChangeLabel(
  result: ReturnType<typeof percentChangeVsBaseline>
): string {
  if (result.type === 'both_zero') return '0%';
  if (result.type === 'baseline_zero') return '—';
  const v = result.value;
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
}
